import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Loader,
  X,
  AlertTriangle,
  ShoppingCart,
  Filter,
  ArrowLeft,
  ChevronDown,
  Info,
  Star,
} from "lucide-react";

// API base URL - can be configured from environment variables
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://apigrand.dodunsoftsolutions.com/api";

// Local storage keys
const STORAGE_KEYS = {
  CATEGORIES: "food_menu_categories",
  MENU_ITEMS: "food_menu_items",
  COMBO_ITEMS: "food_menu_combos",
  LAST_FETCH: "food_menu_last_fetch",
};

// Cache expiration time - 24 hours in milliseconds
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

// Image fallback handler with better error handling
const imageOnErrorHandler = (event) => {
  event.currentTarget.src = "/api/placeholder/300/200";
  event.currentTarget.classList.add("fallback-image");
  event.currentTarget.alt = "Image not available";
};

const FoodMenu = () => {
  // State management
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [comboItems, setComboItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeTab, setActiveTab] = useState("regular"); // 'regular' or 'combo'
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(true);
  const [sortOption, setSortOption] = useState("name"); // 'name', 'price-low', 'price-high'
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [cart, setCart] = useState([]);

  // Enhanced network request with retry logic, timeout, and error handling
  const fetchWithRetry = useCallback(
    async (url, options = {}, maxRetries = 3) => {
      const controller = new AbortController();
      const { signal } = controller;

      // Set timeout to abort request if it takes too long
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(url, { ...options, signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === "AbortError") {
          throw new Error("Request timed out");
        }
        throw error;
      }
    },
    []
  );

  // Load data from local storage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const lastFetchStr = localStorage.getItem(STORAGE_KEYS.LAST_FETCH);
      const lastFetch = lastFetchStr ? parseInt(lastFetchStr, 10) : 0;
      const now = Date.now();

      // Check if cache is still valid (less than 24 hours old)
      if (now - lastFetch < CACHE_EXPIRATION) {
        const categoriesStr = localStorage.getItem(STORAGE_KEYS.CATEGORIES);
        const menuItemsStr = localStorage.getItem(STORAGE_KEYS.MENU_ITEMS);
        const comboItemsStr = localStorage.getItem(STORAGE_KEYS.COMBO_ITEMS);

        if (categoriesStr && menuItemsStr && comboItemsStr) {
          setCategories(JSON.parse(categoriesStr));
          setMenuItems(JSON.parse(menuItemsStr));
          setComboItems(JSON.parse(comboItemsStr));
          setLoading(false);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Error loading from local storage:", error);
      return false;
    }
  }, []);

  // Save data to local storage
  const saveToLocalStorage = useCallback(
    (categoriesData, itemsData, combosData) => {
      try {
        localStorage.setItem(
          STORAGE_KEYS.CATEGORIES,
          JSON.stringify(categoriesData)
        );
        localStorage.setItem(
          STORAGE_KEYS.MENU_ITEMS,
          JSON.stringify(itemsData)
        );
        localStorage.setItem(
          STORAGE_KEYS.COMBO_ITEMS,
          JSON.stringify(combosData)
        );
        localStorage.setItem(STORAGE_KEYS.LAST_FETCH, Date.now().toString());
      } catch (error) {
        console.error("Error saving to local storage:", error);
      }
    },
    []
  );

  // Fetch data from API or local storage
  useEffect(() => {
    const fetchData = async () => {
      // Try to load from local storage first
      if (loadFromLocalStorage()) {
        return;
      }

      // If not in local storage or expired, fetch from API
      try {
        setLoading(true);
        setError(null);

        // Fetch data concurrently for better performance
        const [categoriesData, itemsData, combosData] = await Promise.all([
          fetchWithRetry(`${API_BASE_URL}/kitchen/categories`),
          fetchWithRetry(`${API_BASE_URL}/kitchen/items`),
          fetchWithRetry(`${API_BASE_URL}/kitchen/combos`),
        ]);

        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        } else {
          throw new Error("Invalid categories data format");
        }

        if (Array.isArray(itemsData)) {
          setMenuItems(itemsData);
        } else {
          throw new Error("Invalid menu items data format");
        }

        if (Array.isArray(combosData)) {
          setComboItems(combosData);
        } else {
          throw new Error("Invalid combo items data format");
        }

        // Save fetched data to local storage
        saveToLocalStorage(categoriesData, itemsData, combosData);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(
          "Failed to fetch menu data. Please check your network connection and try again."
        );
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchWithRetry, loadFromLocalStorage, saveToLocalStorage]);


  // ✅ Image resolver: supports ImageKit + local uploads + fallback
  const IMAGE_BASE_URL = "https://apigrand.dodunsoftsolutions.com";

  const getImageUrl = (image) => {
    if (!image) return "/api/placeholder/300/200";

    // ImageKit or any external CDN
    if (image.startsWith("http")) return image;

    // Local stored image (uploads/...)
    return `${IMAGE_BASE_URL}/${image}`;
  };


  // Filter and sort items based on selected category, tab, search term, and availability
  const filteredAndSortedItems = useMemo(() => {
    const items = activeTab === "regular" ? menuItems : comboItems;

    // First filter by availability if needed
    let filtered = showUnavailable
      ? items
      : items.filter((item) => item.isAvailable);

    // Then filter by category
    if (activeCategory !== "all") {
      if (activeTab === "regular") {
        filtered = filtered.filter(
          (item) => item.category && item.category._id === activeCategory
        );
      } else {
        // For combo items, check if any included items belong to the category
        filtered = filtered.filter(
          (combo) =>
            combo.itemsIncluded &&
            combo.itemsIncluded.some(
              (inclusion) =>
                inclusion.item &&
                inclusion.item.category &&
                inclusion.item.category._id === activeCategory
            )
        );
      }
    }

    // Then filter by search term
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const nameMatch =
          item.name && item.name.toLowerCase().includes(lowerSearchTerm);
        const descMatch =
          item.description &&
          item.description.toLowerCase().includes(lowerSearchTerm);

        // For combo items, also check included items
        let includedItemsMatch = false;
        if (activeTab === "combo" && item.itemsIncluded) {
          includedItemsMatch = item.itemsIncluded.some(
            (inclusion) =>
              inclusion.item &&
              inclusion.item.name &&
              inclusion.item.name.toLowerCase().includes(lowerSearchTerm)
          );
        }

        return nameMatch || descMatch || includedItemsMatch;
      });
    }

    // Then sort the filtered items
    return [...filtered].sort((a, b) => {
      switch (sortOption) {
        case "price-low":
          return (
            (activeTab === "regular" ? a.price : a.totalPrice) -
            (activeTab === "regular" ? b.price : b.totalPrice)
          );
        case "price-high":
          return (
            (activeTab === "regular" ? b.price : b.totalPrice) -
            (activeTab === "regular" ? a.price : a.totalPrice)
          );
        case "name":
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }, [
    activeCategory,
    activeTab,
    menuItems,
    comboItems,
    searchTerm,
    showUnavailable,
    sortOption,
  ]);

  // Clear cache and refetch data
  const clearCacheAndRefetch = () => {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });

    setCategories([]);
    setMenuItems([]);
    setComboItems([]);
    setError(null);
    setLoading(true);

    // Trigger refetch by simulating component reload
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Toggle search mode
  const toggleSearch = () => {
    setIsSearching(!isSearching);
    if (isSearching) {
      setSearchTerm("");
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setIsSearching(false);
  };

  // Toggle filters panel
  const toggleFilters = () => {
    setShowFilters(!showFilters);
    setFilterDropdownOpen(false);
  };

  // Add item to cart
  const addToCart = (item) => {
    setCart((prevCart) => [
      ...prevCart,
      { ...item, quantity: 1, id: Date.now() },
    ]);
    // Show a toast notification
    alert(`${item.name} added to cart!`);
  };

  // Handle item click
  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  // Close item detail modal
  const closeItemDetail = () => {
    setSelectedItem(null);
  };

  // Toggle filter dropdown
  const toggleFilterDropdown = () => {
    setFilterDropdownOpen(!filterDropdownOpen);
    setShowFilters(false);
  };

  // Toggle category dropdown
  const toggleCategoryDropdown = () => {
    setCategoryDropdownOpen(!categoryDropdownOpen);
  };

  // Select category
  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setCategoryDropdownOpen(false);
  };

  // Components
  const ErrorDisplay = () => (
    <div className="p-6 bg-red-50 text-red-700 rounded-lg flex flex-col items-center">
      <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
      <h2 className="text-xl font-bold mb-2">Error</h2>
      <p className="text-center mb-4">{error}</p>
      <button
        onClick={clearCacheAndRefetch}
        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
      >
        Try Again
      </button>
    </div>
  );

  const LoadingDisplay = () => (
    <div className="flex flex-col justify-center items-center h-64">
      <Loader className="w-12 h-12 text-red-500 mb-4 animate-spin" />
      <p className="text-gray-600">Loading menu items...</p>
    </div>
  );

  const EmptyResultsDisplay = () => (
    <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-500">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Filter className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium mb-2">No items found</h3>
      <p className="text-center text-gray-500 max-w-md">
        {searchTerm
          ? `No items matching "${searchTerm}" were found. Try a different search term or clear filters.`
          : "No items match your current filters. Try changing your category or filter options."}
      </p>
      <button
        onClick={() => {
          setActiveCategory("all");
          setSearchTerm("");
          setShowUnavailable(true);
        }}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
        Clear All Filters
      </button>
    </div>
  );

  const MenuItemCard = ({ item }) => {
    const price = activeTab === "regular" ? item.price : item.totalPrice;
    const formattedPrice = price ? `₹${price.toFixed(2)}` : "Price unavailable";

    return (
      <div
        className={`border rounded-lg overflow-hidden shadow-md transition-all duration-300 hover:shadow-lg bg-white ${!item.isAvailable ? "opacity-75" : ""
          } cursor-pointer`}
        onClick={() => item.isAvailable && handleItemClick(item)}
      >
        <div className="relative">
          <img
            src={getImageUrl(item.image)}
            alt={item.name}
            className="w-full h-48 object-cover"
            onError={imageOnErrorHandler}
            loading="lazy"
          />
          {!item.isAvailable && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Unavailable
              </span>
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-semibold text-gray-800 line-clamp-1">
              {item.name}
            </h3>
            <span className="font-bold text-red-600">{formattedPrice}</span>
          </div>

          {item.description && (
            <p className="text-gray-600 mb-3 line-clamp-2 text-sm">
              {item.description}
            </p>
          )}

          {activeTab === "regular" && item.category && (
            <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
              {item.category.name}
            </span>
          )}

          {activeTab === "combo" && item.itemsIncluded && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">Includes:</p>
              <div className="flex flex-wrap gap-1">
                {item.itemsIncluded.map((inclusion, idx) => (
                  <span
                    key={idx}
                    className="inline-block bg-red-50 text-red-800 text-xs px-2 py-1 rounded"
                  >
                    {inclusion.quantity || 1} × {inclusion.item?.name || "Item"}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button
            className={`mt-3 w-full py-2 rounded-md flex items-center justify-center text-sm font-medium transition-colors ${item.isAvailable
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            disabled={!item.isAvailable}
            onClick={(e) => {
              e.stopPropagation(); // Prevent the card click event
              if (item.isAvailable) {
                addToCart(item);
              }
            }}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {item.isAvailable ? "Add to Order" : "Unavailable"}
          </button>
        </div>
      </div>
    );
  };

  const ItemDetailModal = ({ item }) => {
    if (!item) return null;

    const price = activeTab === "regular" ? item.price : item.totalPrice;
    const formattedPrice = price ? `₹${price.toFixed(2)}` : "Price unavailable";

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto transition-all duration-300">
          {/* Image Section */}
          <div className="relative">
            <img
              src={getImageUrl(item.image)}
              alt={item.name}
              className="w-full h-60 object-cover rounded-t-2xl"
              onError={imageOnErrorHandler}
            />
            <button
              onClick={closeItemDetail}
              className="absolute top-2 right-2 bg-black bg-opacity-60 text-white rounded-full p-2 hover:bg-opacity-80 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Info Section */}
          <div className="p-6">
            <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
              <h2 className="text-2xl font-semibold text-gray-800">
                {item.name}
              </h2>
              <span className="font-bold text-red-600 text-xl">
                {formattedPrice}
              </span>
            </div>

            {item.description && (
              <p className="text-gray-600 mb-4 leading-relaxed">
                {item.description}
              </p>
            )}

            {activeTab === "regular" && item.category && (
              <div className="mb-4">
                <span className="font-medium text-gray-700">Category: </span>
                <span className="inline-block bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                  {item.category.name}
                </span>
              </div>
            )}

            {activeTab === "combo" && item.itemsIncluded && (
              <div className="mb-4">
                <h3 className="font-medium text-gray-700 mb-2">Includes:</h3>
                <ul className="space-y-1">
                  {item.itemsIncluded.map((inclusion, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded mr-2 text-sm font-medium">
                        {inclusion.quantity || 1}×
                      </span>
                      <span className="text-gray-700">
                        {inclusion.item?.name || "Item"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={() => {
                  addToCart(item);
                  closeItemDetail();
                }}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center justify-center"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Order
              </button>
              <button
                onClick={closeItemDetail}
                className="py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const FiltersPanel = () => (
    <div className="bg-white rounded-lg shadow-lg p-4 mb-6 border border-red-100">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-800">Filter Options</h3>
        <button
          onClick={toggleFilters}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort By
          </label>
          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm focus:border-red-500 focus:ring-red-500"
          >
            <option value="name">Name (A-Z)</option>
            <option value="price-low">Price (Low to High)</option>
            <option value="price-high">Price (High to Low)</option>
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="show-unavailable"
            checked={showUnavailable}
            onChange={(e) => setShowUnavailable(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
          <label
            htmlFor="show-unavailable"
            className="ml-2 block text-sm text-gray-700"
          >
            Show unavailable items
          </label>
        </div>

        <div className="pt-2 border-t border-gray-200">
          <button
            onClick={clearCacheAndRefetch}
            className="text-red-600 text-sm hover:text-red-800 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh Menu Data
          </button>
        </div>
      </div>
    </div>
  );

  const FilterDropdown = () => (
    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-md shadow-lg z-20 border border-gray-200">
      <div className="p-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort By
          </label>
          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
          >
            <option value="name">Name (A-Z)</option>
            <option value="price-low">Price (Low to High)</option>
            <option value="price-high">Price (High to Low)</option>
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="dropdown-show-unavailable"
            checked={showUnavailable}
            onChange={(e) => setShowUnavailable(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
          <label
            htmlFor="dropdown-show-unavailable"
            className="ml-2 block text-sm text-gray-700"
          >
            Show unavailable items
          </label>
        </div>

        <div className="pt-2 border-t border-gray-200">
          <button
            onClick={clearCacheAndRefetch}
            className="text-red-600 text-sm hover:text-red-800 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh Menu Data
          </button>
        </div>
      </div>
    </div>
  );

  const CategoryDropdown = () => (
    <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg z-20 border border-gray-200 max-h-80 overflow-y-auto">
      <div className="py-1">
        <button
          onClick={() => selectCategory("all")}
          className={`w-full text-left px-4 py-2 text-sm ${activeCategory === "all"
            ? "bg-red-50 text-red-700"
            : "text-gray-700 hover:bg-gray-50"
            }`}
        >
          All Categories
        </button>
        {categories.map((category) => (
          <button
            key={category._id}
            onClick={() => selectCategory(category._id)}
            className={`w-full text-left px-4 py-2 text-sm flex items-center ${activeCategory === category._id
              ? "bg-red-50 text-red-700"
              : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            {category.icon && (
              <img
                src={getImageUrl(category.icon)}
                alt={category.name}
                className="w-4 h-4 mr-2 object-contain"
              />
            )}
            {category.name}
          </button>
        ))}
      </div>
    </div>
  );

  // Main render
  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 ">
      <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8 text-red-800">
        Food & Beverages Menu
      </h1>

      {/* Header with search and filter options */}
      <div className="mb-6 sticky top-0 z-10 bg-white bg-opacity-95 backdrop-blur-sm py-3 px-4 rounded-lg shadow-sm border-l-4 border-red-500">
        <div className="flex items-center justify-between">
          {isSearching ? (
            <div className="flex-1 flex items-center">
              <button
                onClick={clearSearch}
                className="mr-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search menu items..."
                className="flex-1 border-0 focus:ring-0 outline-none bg-transparent placeholder-gray-400"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                <button
                  onClick={toggleCategoryDropdown}
                  className="flex items-center text-lg font-semibold text-red-800 hover:text-red-700"
                >
                  {activeCategory === "all"
                    ? "All Categories"
                    : categories.find((c) => c._id === activeCategory)?.name ||
                    "Category"}
                  <ChevronDown className="w-5 h-5 ml-1" />
                </button>
                {categoryDropdownOpen && <CategoryDropdown />}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleSearch}
                  className="p-2 rounded-full hover:bg-red-100"
                  aria-label="Search"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-red-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                </button>
                <div className="relative">
                  <button
                    onClick={toggleFilterDropdown}
                    className="p-2 rounded-full hover:bg-red-100"
                    aria-label="Filter"
                  >
                    <Filter className="w-5 h-5 text-red-600" />
                  </button>
                  {filterDropdownOpen && <FilterDropdown />}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showFilters && <FiltersPanel />}

      {/* Menu tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setActiveTab("regular")}
            className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${activeTab === "regular"
              ? "bg-red-600 text-white border-red-600"
              : "bg-white text-gray-700 border-gray-200 hover:bg-red-50"
              }`}
            aria-current={activeTab === "regular" ? "page" : undefined}
          >
            Individual Items
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("combo")}
            className={`px-4 py-2 text-sm font-medium rounded-r-lg border ${activeTab === "combo"
              ? "bg-red-600 text-white border-red-600"
              : "bg-white text-gray-700 border-gray-200 hover:bg-red-50"
              }`}
            aria-current={activeTab === "combo" ? "page" : undefined}
          >
            Combo Deals
          </button>
        </div>
      </div>

      {/* Category filter - horizontal scrolling */}
      <div className="mb-6 md:mb-8 overflow-x-auto pb-1">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition-colors duration-200 ${activeCategory === "all"
              ? "bg-red-600 text-white"
              : "bg-gray-200 hover:bg-red-100 text-gray-800"
              }`}
            aria-pressed={activeCategory === "all"}
          >
            All Categories
          </button>

          {categories.map((category) => (
            <button
              key={category._id}
              onClick={() => setActiveCategory(category._id)}
              className={`px-3 py-1 rounded-full flex items-center text-sm whitespace-nowrap transition-colors duration-200 ${activeCategory === category._id
                ? "bg-red-600 text-white"
                : "bg-gray-200 hover:bg-red-100 text-gray-800"
                }`}
              aria-pressed={activeCategory === category._id}
            >
              {category.icon && (
                <img
                  src={getImageUrl(category.icon)}
                  alt={category.name}
                  className="w-4 h-4 mr-2 object-contain"
                />
              )}
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Last fetch time */}
      {!loading && !error && (
        <div className="mb-4 text-xs text-gray-500 text-right">
          {localStorage.getItem(STORAGE_KEYS.LAST_FETCH) ? (
            <>
              Data last updated:{" "}
              {new Date(
                parseInt(localStorage.getItem(STORAGE_KEYS.LAST_FETCH), 10)
              ).toLocaleString()}
              <button
                onClick={clearCacheAndRefetch}
                className="ml-2 text-red-600 hover:text-red-800 underline"
              >
                Refresh
              </button>
            </>
          ) : (
            "Using fresh data"
          )}
        </div>
      )}

      {/* Content area */}
      {loading ? (
        <LoadingDisplay />
      ) : error ? (
        <ErrorDisplay />
      ) : (
        <>
          {/* Results count */}
          {!loading && !error && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredAndSortedItems.length}{" "}
              {activeTab === "regular" ? "items" : "combos"}
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          )}

          {/* Menu items grid */}
          {filteredAndSortedItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAndSortedItems.map((item) => (
                <MenuItemCard key={item._id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyResultsDisplay />
          )}
        </>
      )}

      {/* Item detail modal */}
      {selectedItem && <ItemDetailModal item={selectedItem} />}

      {/* Small cart indicator */}
      {cart.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg cursor-pointer hover:bg-red-700 transition-colors">
          <ShoppingCart className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-white text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
            {cart.length}
          </span>
        </div>
      )}

      {/* Click outside handler to close dropdowns */}
      {(filterDropdownOpen || categoryDropdownOpen) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setFilterDropdownOpen(false);
            setCategoryDropdownOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(FoodMenu);
